import {Card, Column, Table} from 'platform/components';
import {Box, Center, getStepsFromPixels, Heading, Show, Space, Spinner} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {
  FastTruckIssue,
  PatchReceiveNoteItem,
  useGetReceiveNoteFastTrackIssuesQuery,
  useGetReceiveNoteFastTrackItemsQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {FastTrackIssueTableRow} from './FastTrackIssueTableRow';
import {FastTrackTableHeader} from './FastTrackTableHeader';
import {ReceiveNoteInformationRow} from './ReceiveNoteInformationRow';

export interface FastTrackIssuesCardProps {
  itemId: string;
  receiveNoteId: string;
  isCollapsedAll: boolean;
  onAcceptIssue: (item: PatchReceiveNoteItem) => void;
  isPending: boolean;
  isReceiveNoteCompleted: boolean;
}

export function FastTrackIssuesCard(props: FastTrackIssuesCardProps) {
  const [collapsed, setCollapsed] = useState(props.isCollapsedAll);
  const [prevIsCollapsedAll, setPrevIsCollapsedAll] = useState(props.isCollapsedAll);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {data: fastTrackItems} = useGetReceiveNoteFastTrackItemsQuery({
    receiveNoteId: props.receiveNoteId,
  });
  const {data: fastTrackIssues} = useGetReceiveNoteFastTrackIssuesQuery({
    receiveNoteId: props.receiveNoteId,
  });

  const item = fastTrackItems?.items[props.itemId];
  const issueIds = fastTrackIssues?.issuesByItem[props.itemId] ?? [];
  const issues = issueIds
    .map((id) => fastTrackIssues?.issues[id])
    .filter(Boolean) as FastTruckIssue[];

  if (props.isCollapsedAll !== prevIsCollapsedAll) {
    setPrevIsCollapsedAll(props.isCollapsedAll);
    setCollapsed(props.isCollapsedAll);
  }

  const columns: Column[] = [
    {
      width: 10,
      element: <FastTrackTableHeader></FastTrackTableHeader>,
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.ftNumber')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.blockedBy')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.ftDate')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.ftQuantity')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.note')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.orderNumber')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.orderDate')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.vehicle')}</FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>
          {i18n.t('entity.warehouse.labels.licencePlate')}
        </FastTrackTableHeader>
      ),
    },
    {
      width: 45,
      element: (
        <FastTrackTableHeader>{i18n.t('entity.warehouse.labels.customer')}</FastTrackTableHeader>
      ),
    },
  ];

  useEffect(
    function observeContainerWidth() {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };

      updateWidth();

      const resizeObserver = new ResizeObserver(() => {
        updateWidth();
      });

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    },
    [collapsed]
  );

  const handleSetCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleOnAccept = () => {
    if (!item) {
      return;
    }
    props.onAcceptIssue({
      receiveNoteItemId: props.itemId,
      isAccepted: !item.isAccepted,
      fastTrackItem: issues.map((fastTruckIssue) => ({
        fastTrackItemId: fastTruckIssue.fastTruckItemId,
        isSelected: fastTruckIssue.isSelected,
      })),
    });
  };

  const handleOnAcceptIssue = (updatedFastTrackItem: FastTruckIssue) => {
    if (!item) {
      return;
    }
    const updatedFastTrackItems = issues.map((fastTruckIssue) => {
      if (fastTruckIssue.fastTruckItemId === updatedFastTrackItem.fastTruckItemId) {
        return {
          fastTrackItemId: fastTruckIssue.fastTruckItemId,
          isSelected: !fastTruckIssue.isSelected,
        };
      }

      return {
        fastTrackItemId: fastTruckIssue.fastTruckItemId,
        isSelected: fastTruckIssue.isSelected,
      };
    });

    props.onAcceptIssue({
      receiveNoteItemId: props.itemId,
      isAccepted: item.isAccepted,
      fastTrackItem: updatedFastTrackItems,
    });
  };

  if (!item) {
    return null;
  }

  return (
    <Box ref={containerRef} position="relative">
      <Card key={props.itemId} variant="inlineGrey">
        <ReceiveNoteInformationRow
          receiveNoteItem={item}
          onToggleCollapse={() => handleSetCollapse()}
          onAccept={handleOnAccept}
          isCollapsed={collapsed}
          isReceiveNoteCompleted={props.isReceiveNoteCompleted}
          data-testid={`fastTrackItem-${props.itemId}`}
        />
        <Space vertical={4} />
        <Show when={!collapsed}>
          <Show when={issues.length > 0}>
            <Box
              overflowX="auto"
              width={containerWidth ? getStepsFromPixels(containerWidth - 48) : '100%'}
            >
              <Table variant="bordered" columns={columns} tableLayout="fixed">
                {issues.map((fastTruckIssue, index) => (
                  <FastTrackIssueTableRow
                    key={index} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                    fastTruckIssue={fastTruckIssue}
                    onAcceptIssue={handleOnAcceptIssue}
                    isReceiveNoteCompleted={props.isReceiveNoteCompleted}
                  />
                ))}
              </Table>
            </Box>
          </Show>
          <Show when={issues.length === 0}>
            <Center height={10}>
              <Heading alternative size={5}>
                {i18n.t('entity.warehouse.labels.noFastTrackIssues')}
              </Heading>
            </Center>
          </Show>
        </Show>
      </Card>
      <Show when={props.isPending}>
        <Spinner variant="overlay" />
      </Show>
    </Box>
  );
}
